/**
 * Ramp View Config Store - Persists which label fields and warnings are shown on all aircraft
 *
 * FBO managers can toggle data fields (tail number, client, contract info, etc.)
 * and warning labels (status-row based warnings with full/compact/off display)
 * to display as compact sticky labels above every aircraft on the ramp map.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { StatusRowGroup } from '@/lib/utils/compute-status-rows'

// ============================================
// Data Label Field Types & Registry
// ============================================

export type RampLabelField =
  | 'tailNumber'
  | 'typeCode'
  | 'ownerName'
  | 'ownerContact'
  | 'contractPrice'
  | 'contractStart'
  | 'contractEnd'
  | 'areaUnit'
  | 'positionType'
  | 'insuranceExpiry'
  | 'contractType'
  | 'contractStatus'
  | 'contractNumber'
  | 'fuelProgress'

export type RampLabelGroup = 'aircraft' | 'placement' | 'contract' | 'fuel'

export interface RampLabelFieldConfig {
  field: RampLabelField
  label: string
  tooltip: string
  group: RampLabelGroup
}

/**
 * Single source of truth for all available label fields.
 * Drives both the config panel checkboxes and the label renderer.
 */
export const RAMP_LABEL_FIELDS: RampLabelFieldConfig[] = [
  // Aircraft group
  { field: 'tailNumber', label: 'Tail Number', tooltip: 'Aircraft tail/registration number', group: 'aircraft' },
  { field: 'typeCode', label: 'Type Code', tooltip: 'ICAO aircraft type designator', group: 'aircraft' },
  { field: 'ownerName', label: 'Owner / Client', tooltip: 'Aircraft owner or client name', group: 'aircraft' },
  { field: 'ownerContact', label: 'Contact Info', tooltip: 'Owner email or phone number', group: 'aircraft' },
  { field: 'insuranceExpiry', label: 'Insurance Expiry', tooltip: 'Insurance expiration date', group: 'aircraft' },

  // Placement group
  { field: 'areaUnit', label: 'Area / Unit', tooltip: 'Hangar area and unit code', group: 'placement' },
  { field: 'positionType', label: 'Position Type', tooltip: 'Hangar, ramp, maintenance, etc.', group: 'placement' },

  // Contract group
  { field: 'contractType', label: 'Contract Type', tooltip: 'Lease or transient stay', group: 'contract' },
  { field: 'contractNumber', label: 'Contract / Booking #', tooltip: 'Lease contract or booking number', group: 'contract' },
  { field: 'contractStatus', label: 'Contract Status', tooltip: 'Lease or transient stay status', group: 'contract' },
  { field: 'contractPrice', label: 'Contract Rate', tooltip: 'Monthly lease rate or nightly rate', group: 'contract' },
  { field: 'contractStart', label: 'Contract Start', tooltip: 'Lease start date or check-in', group: 'contract' },
  { field: 'contractEnd', label: 'Contract End', tooltip: 'Lease end date or expected check-out', group: 'contract' },

  // Fuel group
  { field: 'fuelProgress', label: 'Fuel Progress', tooltip: 'Fuel purchased vs monthly commitment', group: 'fuel' },
]

/** Group labels for the config panel */
export const RAMP_LABEL_GROUPS: { key: RampLabelGroup; label: string }[] = [
  { key: 'aircraft', label: 'Aircraft' },
  { key: 'placement', label: 'Placement' },
  { key: 'contract', label: 'Contract' },
  { key: 'fuel', label: 'Fuel' },
]

// ============================================
// Warning Label Field Types & Registry
// @deprecated — Replaced by status-row-based warnings.
// Kept for backward compat during migration; will be removed.
// ============================================

/** @deprecated Use StatusRowGroup from compute-status-rows instead */
export type RampWarningField =
  // Critical
  | 'collision_active'
  | 'insurance_expired'
  | 'registration_expired'
  | 'payment_overdue'
  | 'no_contract'
  | 'contract_expired'
  | 'clearance_violation'
  // Warning
  | 'contract_expiring'
  | 'insurance_expiring'
  | 'departing_today'
  | 'overstaying'
  | 'no_departure_date'
  | 'checkout_today'
  // Attention
  | 'no_owner'
  | 'incomplete_record'
  | 'hangar_queen'
  | 'fuel_commitment_behind'
  | 'wrong_unit'
  // Informational
  | 'vip_client'
  | 'auto_renew_upcoming'
  | 'repeat_visitor'
  | 'maintenance_scheduled'
  | 'recently_fueled'

/** @deprecated Use StatusRowSeverity from aircraft-card types instead */
export type WarningSeverity = 'critical' | 'warning' | 'attention' | 'info'
/** @deprecated */
export type WarningGroup = 'contract' | 'operations' | 'financial' | 'fuel' | 'client'

/** @deprecated */
export interface RampWarningFieldConfig {
  field: RampWarningField
  label: string
  tooltip: string
  severity: WarningSeverity
  group: WarningGroup
  available: boolean
}

/** @deprecated — Kept for reference; no longer drives map labels */
export const RAMP_WARNING_FIELDS: RampWarningFieldConfig[] = [
  { field: 'no_contract', label: 'No Contract', tooltip: 'Placed aircraft has no active lease or transient contract', severity: 'critical', group: 'contract', available: true },
  { field: 'contract_expired', label: 'Contract Expired', tooltip: 'Lease contract end date has passed', severity: 'critical', group: 'contract', available: true },
  { field: 'contract_expiring', label: 'Contract Expiring', tooltip: 'Lease or contract expires within 30 days', severity: 'warning', group: 'contract', available: true },
  { field: 'overstaying', label: 'Overstaying', tooltip: 'Transient stay past expected checkout date', severity: 'warning', group: 'contract', available: true },
  { field: 'departing_today', label: 'Departing Today', tooltip: 'Transient stay expected checkout is today or tomorrow', severity: 'warning', group: 'contract', available: true },
  { field: 'checkout_today', label: 'Checkout Today', tooltip: 'Transient checkout scheduled for today', severity: 'warning', group: 'contract', available: true },
  { field: 'no_departure_date', label: 'No Departure Date', tooltip: 'Open-ended stay with no expected departure', severity: 'warning', group: 'contract', available: true },
  { field: 'auto_renew_upcoming', label: 'Auto-Renew', tooltip: 'Lease auto-renew within 30 days', severity: 'info', group: 'contract', available: true },
  { field: 'collision_active', label: 'Active Collision', tooltip: 'Aircraft has active collision with another aircraft', severity: 'critical', group: 'operations', available: true },
  { field: 'clearance_violation', label: 'Clearance Violation', tooltip: 'Aircraft too close to another (below minimum clearance)', severity: 'critical', group: 'operations', available: false },
  { field: 'wrong_unit', label: 'Wrong Unit', tooltip: 'Placed outside assigned contract unit', severity: 'attention', group: 'operations', available: true },
  { field: 'hangar_queen', label: 'Hangar Queen', tooltip: 'Aircraft has not moved in 90+ days', severity: 'attention', group: 'operations', available: false },
  { field: 'maintenance_scheduled', label: 'Maintenance', tooltip: 'Scheduled maintenance upcoming', severity: 'info', group: 'operations', available: false },
  { field: 'insurance_expired', label: 'Insurance Expired', tooltip: 'Aircraft insurance has expired', severity: 'critical', group: 'financial', available: true },
  { field: 'payment_overdue', label: 'Payment Overdue', tooltip: 'Outstanding payment past due date', severity: 'critical', group: 'financial', available: false },
  { field: 'insurance_expiring', label: 'Insurance Expiring', tooltip: 'Aircraft insurance expires within 30 days', severity: 'warning', group: 'financial', available: true },
  { field: 'registration_expired', label: 'Registration Expired', tooltip: 'Aircraft registration has expired', severity: 'critical', group: 'financial', available: false },
  { field: 'fuel_commitment_behind', label: 'Fuel Behind Pace', tooltip: 'Behind on monthly fuel purchase commitment', severity: 'warning', group: 'fuel', available: true },
  { field: 'recently_fueled', label: 'Recently Fueled', tooltip: 'Aircraft was fueled recently', severity: 'info', group: 'fuel', available: false },
  { field: 'no_owner', label: 'No Owner / Client', tooltip: 'No owner or client assigned to aircraft', severity: 'attention', group: 'client', available: true },
  { field: 'incomplete_record', label: 'Incomplete Record', tooltip: 'Aircraft record is missing critical fields', severity: 'attention', group: 'client', available: false },
  { field: 'vip_client', label: 'VIP Client', tooltip: 'Aircraft belongs to VIP client — priority service', severity: 'info', group: 'client', available: false },
  { field: 'repeat_visitor', label: 'Repeat Visitor', tooltip: 'Frequent transient visitor — conversion opportunity', severity: 'info', group: 'client', available: false },
]

/** @deprecated */
export const RAMP_WARNING_GROUPS: { key: WarningGroup; label: string; icon: string }[] = [
  { key: 'contract', label: 'Contract & Stays', icon: 'FileText' },
  { key: 'operations', label: 'Operations & Safety', icon: 'Settings' },
  { key: 'financial', label: 'Financial', icon: 'DollarSign' },
  { key: 'fuel', label: 'Fuel', icon: 'Fuel' },
  { key: 'client', label: 'Client & Data', icon: 'Users' },
]

// ============================================
// Status-row warning display configuration
// ============================================

export type WarningDisplayMode = 'full' | 'compact' | 'off'

/** Status row groups for the map label config panel */
export const STATUS_GROUP_CONFIG: { key: StatusRowGroup; label: string }[] = [
  { key: 'contract', label: 'Contract' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'other', label: 'Other' },
  { key: 'info', label: 'Info' },
]

/**
 * Individual status row items for granular config.
 * `key` matches StatusRowDef.key from compute-status-rows.ts.
 * `severity` is the representative severity for the colored dot.
 */
export type StatusRowSeverityHint = 'critical' | 'warning' | 'notice' | 'info'

export interface StatusRowItemConfig {
  key: string
  label: string
  group: StatusRowGroup
  severity: StatusRowSeverityHint
}

export const STATUS_ROW_ITEMS: StatusRowItemConfig[] = [
  // ── Contract ──
  { key: 'lease_expired',         label: 'Lease Expired',       group: 'contract', severity: 'critical' },
  { key: 'lease_terminated',      label: 'Lease Terminated',    group: 'contract', severity: 'critical' },
  { key: 'lease_completed',       label: 'Lease Completed',     group: 'contract', severity: 'warning' },
  { key: 'lease_cancelled',       label: 'Lease Cancelled',     group: 'contract', severity: 'info' },
  { key: 'lease_inactive',        label: 'Lease Inactive',      group: 'contract', severity: 'info' },
  { key: 'contract_expiring',     label: 'Contract Expiring',   group: 'contract', severity: 'warning' },
  { key: 'no_contract',           label: 'No Contract',         group: 'contract', severity: 'critical' },
  { key: 'no_contract_fresh',     label: 'No Contract (New)',   group: 'contract', severity: 'critical' },
  { key: 'overstaying',           label: 'Overstaying',         group: 'contract', severity: 'warning' },
  { key: 'transient_last_day',    label: 'Last Day',            group: 'contract', severity: 'warning' },
  { key: 'transient_booked_overdue', label: 'Booking Overdue',  group: 'contract', severity: 'warning' },
  { key: 'arriving_today',        label: 'Arriving Today',      group: 'contract', severity: 'notice' },
  { key: 'arriving_tomorrow',     label: 'Arriving Tomorrow',   group: 'contract', severity: 'notice' },

  // ── Fuel ──
  { key: 'fuel_at_risk',          label: 'Fuel At Risk',        group: 'fuel',     severity: 'warning' },
  { key: 'fuel_behind',           label: 'Fuel Behind Pace',    group: 'fuel',     severity: 'notice' },
  { key: 'fuel_activity',         label: 'Fuel Activity',       group: 'fuel',     severity: 'info' },

  // ── Other ──
  { key: 'collision',             label: 'Collision',           group: 'other',    severity: 'critical' },
  { key: 'insurance_expired',     label: 'Insurance Expired',   group: 'other',    severity: 'critical' },
  { key: 'insurance_expiring',    label: 'Insurance Expiring',  group: 'other',    severity: 'warning' },
  { key: 'return_overdue',        label: 'Return Overdue',      group: 'other',    severity: 'warning' },
  { key: 'departing_today',       label: 'Departing Today',     group: 'other',    severity: 'notice' },
  { key: 'departing_tomorrow',    label: 'Departing Tomorrow',  group: 'other',    severity: 'notice' },
  { key: 'lease_no_departure',    label: 'No Departure Sched.', group: 'other',    severity: 'notice' },
  { key: 'lease_ended_no_departure', label: 'No Departure (Ended)', group: 'other', severity: 'warning' },
  { key: 'no_owner',              label: 'No Owner',            group: 'other',    severity: 'info' },

  // ── Info ──
  { key: 'special_requests',      label: 'Special Requests',    group: 'info',     severity: 'info' },
]

const DEFAULT_ENABLED_STATUS_GROUPS: StatusRowGroup[] = ['contract', 'fuel', 'other']

// ============================================
// Aircraft Render Mode
// ============================================

export type AircraftRenderMode = 'full' | 'simplified'

// ============================================
// Store
// ============================================

const DEFAULT_ENABLED_FIELDS: RampLabelField[] = ['tailNumber']

/** @deprecated — Only kept for persist migration */
const DEFAULT_ENABLED_WARNINGS: RampWarningField[] = [
  'collision_active',
  'no_contract',
  'contract_expired',
  'contract_expiring',
  'insurance_expired',
  'overstaying',
]

interface RampViewConfigState {
  enabledFields: RampLabelField[]
  isPanelOpen: boolean
  aircraftRenderMode: AircraftRenderMode
  showAreaLabels: boolean
  showUnitLabels: boolean
  showLeaderLines: boolean
  /** Show 2D top-view PNG sprites while 3D models load (progressive loading) */
  showSpritePreviews: boolean
  /** Show building roofs (arch shells, future flat roofs) */
  showRoofs: boolean
  collapsedGroups: string[]
  /** @deprecated — Replaced by warningDisplayMode + enabledStatusGroups */
  enabledWarnings: RampWarningField[]
  /** @deprecated — Replaced by warningDisplayMode */
  showWarnings: boolean
  /** Display mode for status-row warnings on map labels */
  warningDisplayMode: WarningDisplayMode
  /** Which status row groups to show on map labels */
  enabledStatusGroups: StatusRowGroup[]
  /** Individual status row keys to hide (blocklist — all enabled by default) */
  disabledStatusRowKeys: string[]
}

interface RampViewConfigActions {
  toggleField: (field: RampLabelField) => void
  setFieldEnabled: (field: RampLabelField, enabled: boolean) => void
  setPanelOpen: (open: boolean) => void
  resetToDefaults: () => void
  setAircraftRenderMode: (mode: AircraftRenderMode) => void
  setShowAreaLabels: (show: boolean) => void
  setShowUnitLabels: (show: boolean) => void
  setShowLeaderLines: (show: boolean) => void
  setShowSpritePreviews: (show: boolean) => void
  setShowRoofs: (show: boolean) => void
  toggleGroupCollapsed: (groupKey: string) => void
  /** @deprecated — Use setWarningDisplayMode instead */
  toggleWarning: (field: RampWarningField) => void
  /** @deprecated — Use setWarningDisplayMode instead */
  setShowWarnings: (show: boolean) => void
  setWarningDisplayMode: (mode: WarningDisplayMode) => void
  toggleStatusGroup: (group: StatusRowGroup) => void
  toggleStatusRowKey: (key: string) => void
  setStatusGroupEnabled: (group: StatusRowGroup, enabled: boolean) => void
}

export const useRampViewConfigStore = create<RampViewConfigState & RampViewConfigActions>()(
  devtools(
    persist(
      (set) => ({
        enabledFields: DEFAULT_ENABLED_FIELDS,
        isPanelOpen: false,
        aircraftRenderMode: 'full' as AircraftRenderMode,
        showAreaLabels: true,
        showUnitLabels: false,
        showLeaderLines: false,
        showSpritePreviews: true,
        showRoofs: true,
        enabledWarnings: DEFAULT_ENABLED_WARNINGS,
        showWarnings: true,
        collapsedGroups: [] as string[],
        warningDisplayMode: 'full' as WarningDisplayMode,
        enabledStatusGroups: DEFAULT_ENABLED_STATUS_GROUPS as StatusRowGroup[],
        disabledStatusRowKeys: [] as string[],

        toggleField: (field) =>
          set((state) => ({
            enabledFields: state.enabledFields.includes(field)
              ? state.enabledFields.filter((f) => f !== field)
              : [...state.enabledFields, field],
          })),

        setFieldEnabled: (field, enabled) =>
          set((state) => ({
            enabledFields: enabled
              ? state.enabledFields.includes(field)
                ? state.enabledFields
                : [...state.enabledFields, field]
              : state.enabledFields.filter((f) => f !== field),
          })),

        setPanelOpen: (open) => set({ isPanelOpen: open }),

        resetToDefaults: () => set({
          enabledFields: DEFAULT_ENABLED_FIELDS,
          aircraftRenderMode: 'full',
          showAreaLabels: true,
          showUnitLabels: false,
          showLeaderLines: false,
          showSpritePreviews: true,
          showRoofs: true,
          enabledWarnings: DEFAULT_ENABLED_WARNINGS,
          showWarnings: true,
          collapsedGroups: [],
          warningDisplayMode: 'full',
          enabledStatusGroups: DEFAULT_ENABLED_STATUS_GROUPS,
          disabledStatusRowKeys: [],
        }),

        setAircraftRenderMode: (mode) => set({ aircraftRenderMode: mode }),

        setShowAreaLabels: (show) => set({ showAreaLabels: show }),

        setShowUnitLabels: (show) => set({ showUnitLabels: show }),

        setShowLeaderLines: (show) => set({ showLeaderLines: show }),

        setShowSpritePreviews: (show) => set({ showSpritePreviews: show }),

        setShowRoofs: (show) => set({ showRoofs: show }),

        toggleWarning: (field) =>
          set((state) => ({
            enabledWarnings: state.enabledWarnings.includes(field)
              ? state.enabledWarnings.filter((f) => f !== field)
              : [...state.enabledWarnings, field],
          })),

        setShowWarnings: (show) => set({ showWarnings: show }),

        setWarningDisplayMode: (mode) => set({ warningDisplayMode: mode }),

        toggleStatusGroup: (group) =>
          set((state) => ({
            enabledStatusGroups: state.enabledStatusGroups.includes(group)
              ? state.enabledStatusGroups.filter((g) => g !== group)
              : [...state.enabledStatusGroups, group],
          })),

        toggleStatusRowKey: (key) =>
          set((state) => ({
            disabledStatusRowKeys: state.disabledStatusRowKeys.includes(key)
              ? state.disabledStatusRowKeys.filter((k) => k !== key)
              : [...state.disabledStatusRowKeys, key],
          })),

        setStatusGroupEnabled: (group, enabled) =>
          set((state) => {
            const keysInGroup = STATUS_ROW_ITEMS
              .filter((item) => item.group === group)
              .map((item) => item.key)

            // Enable group if not already
            const newEnabledGroups = enabled
              ? state.enabledStatusGroups.includes(group)
                ? state.enabledStatusGroups
                : [...state.enabledStatusGroups, group]
              : state.enabledStatusGroups.filter((g) => g !== group)

            // When enabling: remove all group keys from blocklist
            // When disabling: add all group keys to blocklist
            const newDisabled = enabled
              ? state.disabledStatusRowKeys.filter((k) => !keysInGroup.includes(k))
              : [
                  ...state.disabledStatusRowKeys.filter((k) => !keysInGroup.includes(k)),
                  ...keysInGroup,
                ]

            return {
              enabledStatusGroups: newEnabledGroups,
              disabledStatusRowKeys: newDisabled,
            }
          }),

        toggleGroupCollapsed: (groupKey) =>
          set((state) => ({
            collapsedGroups: state.collapsedGroups.includes(groupKey)
              ? state.collapsedGroups.filter((k) => k !== groupKey)
              : [...state.collapsedGroups, groupKey],
          })),
      }),
      {
        name: 'planmax-ramp-view-config',
        version: 2,
        migrate: (persisted: unknown, version: number) => {
          const state = persisted as Record<string, unknown>
          if (version < 2) {
            // Migrate from v1 (showWarnings + enabledWarnings) to v2 (warningDisplayMode + enabledStatusGroups)
            const oldShowWarnings = state.showWarnings as boolean | undefined
            state.warningDisplayMode = oldShowWarnings === false ? 'off' : 'full'
            state.enabledStatusGroups = DEFAULT_ENABLED_STATUS_GROUPS
            state.disabledStatusRowKeys = []
          }
          return state as RampViewConfigState & RampViewConfigActions
        },
        partialize: (state) => ({
          enabledFields: state.enabledFields,
          aircraftRenderMode: state.aircraftRenderMode,
          showAreaLabels: state.showAreaLabels,
          showUnitLabels: state.showUnitLabels,
          showLeaderLines: state.showLeaderLines,
          showSpritePreviews: state.showSpritePreviews,
          showRoofs: state.showRoofs,
          enabledWarnings: state.enabledWarnings,
          showWarnings: state.showWarnings,
          collapsedGroups: state.collapsedGroups,
          warningDisplayMode: state.warningDisplayMode,
          enabledStatusGroups: state.enabledStatusGroups,
          disabledStatusRowKeys: state.disabledStatusRowKeys,
        }),
      }
    ),
    { name: 'ramp-view-config' }
  )
)
